import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ButtonArrow from "../../icons/ButtonArrow";

const ASSET_V = "1";
const asset = (path) => `${path}?v=${ASSET_V}`;

const GRID_COLUMN_QUERIES = [
    { query: "(min-width: 1440px)", columns: 8 },
    { query: "(min-width: 768px)", columns: 4 },
    { query: "", columns: 2 },
];

function useGridColumns() {
    const getColumns = () => {
        for (const { query, columns } of GRID_COLUMN_QUERIES) {
            if (!query || window.matchMedia(query).matches) return columns;
        }
        return 2;
    };

    const [columns, setColumns] = useState(getColumns);

    useEffect(() => {
        const mediaQueries = GRID_COLUMN_QUERIES.filter(({ query }) => query).map(({ query }) =>
            window.matchMedia(query),
        );

        const update = () => setColumns(getColumns());

        mediaQueries.forEach((mq) => mq.addEventListener("change", update));
        return () => mediaQueries.forEach((mq) => mq.removeEventListener("change", update));
    }, []);

    return columns;
}

const CLIENTS = [
    {
        src: asset("/assets/image/casestudy/acer/acer-logo.svg"),
        alt: "Acer Electric logo",
        href: "/portfolio/acer-electric",
        width: 120,
    },
    {
        src: asset("/assets/image/clients/yogabar.svg"),
        alt: "Yogabar logo",
        href: "/portfolio/yogabar",
        width: 90,
    },
    {
        src: asset("/assets/image/home/featured-testimonial/uppercase/logo.svg"),
        alt: "Uppercase logo",
        href: "/portfolio/uppercase",
        width: 140,
    },
    {
        src: asset("/assets/image/clients/mtr.svg"),
        alt: "MTR Foods logo",
        href: "https://www.mtrfoods.com/",
        external: true,
        width: 85,
    },
    {
        src: asset("/assets/image/clients/dr-vaidyas.svg"),
        alt: "Dr Vaidyas logo",
        href: "/portfolio/dr-vaidya",
        width: 150,
    },
    {
        src: asset("/assets/image/clients/hamdard.svg"),
        alt: "Hamdard logo",
        href: "/portfolio/hamdard",
        width: 100,
    },
    {
        src: asset("/assets/image/clients/technosport.svg"),
        alt: "Technosport logo",
        href: "/portfolio/technosport",
        width: 135,
    },
    {
        src: asset("/assets/image/clients/kayura.svg"),
        alt: "Kayura logo",
        href: "/portfolio/label/kayura-effect",
        width: 165,
    },
    {
        src: asset("/assets/image/clients/anand.svg"),
        alt: "Anand Sweets logo",
        href: "/portfolio/anand-sweets",
        width: 170,
    },
    {
        src: asset("/assets/image/clients/shree-anandhaash.svg"),
        alt: "Anandhaas Sweets logo",
        href: "/portfolio/anandhaas",
        width: 150,
    },
    {
        src: asset("/assets/image/clients/om.svg"),
        alt: "Om Sweets logo",
        href: "/portfolio/om-sweets",
        width: 90,
    },
    {
        src: asset("/assets/image/clients/healing_hands.svg"),
        alt: "Healing Hands logo",
        href: "/portfolio/healing-hands",
        width: 130,
    },
    {
        src: asset("/assets/image/clients/hyfun.svg"),
        alt: "HyFun Foods logo",
        href: "/portfolio/hyfun",
        width: 115,
    },
    {
        src: asset("/assets/image/casestudy/fruzo/fruzo-logo.svg"),
        alt: "Fruzo logo",
        href: "/portfolio/fruzo",
        width: 125,
    },
    {
        src: asset("/assets/image/clients/repeatgud.svg"),
        alt: "Repeat Gud logo",
        href: "/portfolio/repeat-gud",
        width: 160,
    },
    {
        src: asset("/assets/image/clients/ras.svg"),
        alt: "RAS Beauty logo",
        href: "/portfolio/3d/ras",
        width: 105,
    },
    {
        src: asset("/assets/image/clients/oval.svg"),
        alt: "Oval Health logo",
        href: "/portfolio/oval",
        width: 165,
    },
    {
        src: asset("/assets/image/casestudy/fragstalk/fragstalk-logo.svg"),
        alt: "Fragstalk logo",
        href: "/portfolio/fragstalk",
        width: 125,
    },
    {
        src: asset("/assets/image/clients/bikanerwala.svg"),
        alt: "Bikanerwala logo",
        width: 120,
    },
    {
        src: asset("/assets/image/clients/neo-pay.svg"),
        alt: "Neo Pay logo",
        width: 130,
    },
    {
        src: asset("/assets/image/clients/11-11.svg"),
        alt: "11:11 logo",
        width: 110,
    },
    {
        src: asset("/assets/image/clients/ebg-group.svg"),
        alt: "EBG Group logo",
        width: 95,
    },
];

/** Below sm, logos use ~58% of desktop width so sizes stay different but smaller. */
const LOGO_WIDTH_SM_RATIO = 0.80;

function ClientsLogoCard({ client }) {
    const logoStyle = client.width
        ? {
            ["--logo-width"]: `${client.width}px`,
            ["--logo-width-sm"]: `${client.widthSm ?? Math.max(48, Math.round(client.width * LOGO_WIDTH_SM_RATIO))}px`,
        }
        : undefined;

    const card = (
        <div className="product_logo">
            <div className="logo_box" style={logoStyle}>
                <img
                    src={client.src}
                    alt={client.alt}
                    className="home_logos"
                    decoding="async"
                />
            </div>
        </div>
    );

    if (!client.href) return card;

    if (client.external) {
        return (
            <a
                href={client.href}
                target="_blank"
                rel="noopener noreferrer"
                className="image_color bg-white home-clients-logo-card"
            >
                {card}
            </a>
        );
    }

    return (
        <Link to={client.href} className="image_color bg-white home-clients-logo-card">
            {card}
        </Link>
    );
}

function ClientsViewMoreCard() {
    const preloadClients = () => {
        import("../../../pages/Clients");
    };

    return (
        <div className="product_logo home-clients-logo-cta">
            <h3 className="logo_h4 logo_content">Brands We’ve Built</h3>
            <div className="logo_btn">
                <Link
                    to="/clients"
                    className="btn submit_btn"
                    onMouseEnter={preloadClients}
                    onTouchStart={preloadClients}
                    onFocus={preloadClients}
                >
                    View All
                    <span className="submit_arrow_wrap" aria-hidden="true">
                        <ButtonArrow className="submit_arrow" fill="#417B5A" />
                        <ButtonArrow className="submit_hover" fill="#ffffff" />
                    </span>
                </Link>
            </div>
        </div>
    );
}

function getCtaPlacement(columns, itemCount) {
    const remainder = itemCount % columns;
    const ctaSpan = remainder === 0 ? columns : columns - remainder;
    const ctaStartCol = remainder === 0 ? 1 : remainder + 1;
    return { ctaSpan, ctaStartCol };
}

/** Homepage client logo grid — Figma node 9752:3381 */
export default function HomeClientsLogoGrid() {
    const columns = useGridColumns();
    const { ctaSpan, ctaStartCol } = getCtaPlacement(columns, CLIENTS.length);
    const lastRowIndex = Math.ceil((CLIENTS.length + ctaSpan) / columns) - 1;

    return (
        <section className="home-clients-logo-grid tw:bg-white tw:py-10 tw:sm:py-12 tw:md:py-16 tw:3xl:py-[100px]">
            <div className="home-clients-logo-grid__inner tw:px-4 tw:sm:px-5 tw:md:px-10 tw:1xl:px-[100px]">
                <h2 className="tw:m-0 tw:mb-10 tw:w-full tw:font-medium tw:text-[#222] tw:text-[28px] tw:xs:text-[32px] tw:sm:mb-12 tw:sm:text-[clamp(36px,4.5vw,52px)] tw:md:mb-14 tw:3xl:mb-[70px] tw:3xl:text-[64px] tw:leading-[1.1] tw:text-balance">
                    Where Visionary
                    <br />
                    Brands Meet Execution
                </h2>
            </div>

            <div className="home-clients-logo-grid__grid-wrap">
                <div className="home-clients-logo-grid__rule" aria-hidden="true" />
                <div className="home-clients-logo-grid__board-shell tw:px-4 tw:sm:px-5 tw:md:px-10 tw:1xl:px-[100px]">
                    <div
                        className="home-clients-logo-grid__board"
                        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
                    >
                        {CLIENTS.map((client, index) => {
                            const rowIndex = Math.floor(index / columns);
                            const hasRowDivider = rowIndex < lastRowIndex;

                            return (
                                <div
                                    key={client.alt}
                                    className={`home-clients-logo-cell${hasRowDivider ? " home-clients-logo-cell--row-divider" : ""}`}
                                >
                                    <ClientsLogoCard client={client} />
                                </div>
                            );
                        })}
                        <div
                            className="home-clients-logo-cell home-clients-logo-cell--cta"
                            style={{ gridColumn: `${ctaStartCol} / span ${ctaSpan}` }}
                        >
                            <ClientsViewMoreCard />
                        </div>
                    </div>
                </div>
                <div className="home-clients-logo-grid__rule" aria-hidden="true" />
            </div>
        </section>
    );
}
